/*
  Gaia Iluka — Where To Find Us: the map of South Korea
  File: assets/gi-cw-atlas.js

  Scoped to sections/gi-cw-map.liquid. No framework, no map library and no
  network calls: the geography is static SVG already in the page
  (snippets/gi-cw-atlas-geometry.liquid, Natural Earth 1:10m).

  CAMERA. The SVG's viewBox is the camera: a centre (cx, cy) in map units
  and s, map units per screen pixel. On load and on reset it is fitted to
  South Korea (the data-subject frame). Zoom is limited at both ends: never
  further out than the country, never closer than roughly city scale. Pan
  is held inside the drawn area, so the edge of the data is never shown.

  PROJECTION. Lambert conformal conic on a sphere, the same formula d3-geo
  uses, with the constants from data-proj written by the offline build
  (references/where-to-find-us-atlas/build-korea.mjs). The build checks
  this formula against d3, so markers land exactly on the geography.

  DATA. Partners come from the server-rendered JSON blocks; their profile
  cards are pre-rendered and only shown or hidden here. Search, city
  suggestions, the experience filter, the gallery and View list keep the
  behaviour of the earlier scripts. Nothing about any partner, city or
  province is written into this file.
*/
(function () {
  'use strict';

  var RAD = Math.PI / 180;
  var HALF = Math.PI / 2;
  var EARTH_KM = 6371;

  /* Reference places for orientation (never markers): real coordinates,
     translated names from the section. */
  var REFERENCE = [
    { key: 'seoul', kind: 'town', lon: 126.978, lat: 37.566 },
    { key: 'busan', kind: 'town', lon: 129.075, lat: 35.18 },
    { key: 'jeju', kind: 'island', lon: 126.55, lat: 33.38 }
  ];

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function norm(s) { return String(s == null ? '' : s).toLowerCase().trim(); }
  function experienceOf(category) { return category === 'retail' ? 'buy' : 'eat'; }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  function makeProjection(m) {
    function tany(y) { return Math.tan((HALF + y) / 2); }
    return {
      project: function (lon, lat) {
        var x = (lon - m.LON0) * RAD;
        var r = m.F / Math.pow(tany(lat * RAD), m.N);
        return [m.TX + m.K * r * Math.sin(m.N * x), m.TY - m.K * (m.F - r * Math.cos(m.N * x))];
      },
      latAt: function (Y, X) {
        var rx = (X - m.TX) / m.K;
        var fy = m.F - (m.TY - Y) / m.K;
        var r = (m.N > 0 ? 1 : -1) * Math.sqrt(rx * rx + fy * fy);
        return (2 * Math.atan(Math.pow(m.F / r, 1 / m.N)) - HALF) / RAD;
      },
      /* map units per km at a latitude (the conic's local scale factor) */
      unitsPerKm: function (lat) {
        var phi = lat * RAD;
        var k = (m.N * m.F) / (Math.cos(phi) * Math.pow(tany(phi), m.N));
        return (m.K * k) / EARTH_KM;
      }
    };
  }

  function init(root) {
    if (root.getAttribute('data-gi-cw-ready') === 'true') return;
    var svg = root.querySelector('[data-gi-cw-svg]');
    var viewport = root.querySelector('[data-gi-cw-viewport]');
    if (!svg || !viewport) return;
    var meta, subject;
    try {
      meta = JSON.parse(svg.getAttribute('data-proj'));
      subject = JSON.parse(svg.getAttribute('data-subject'));
    } catch (e) { return; }
    root.setAttribute('data-gi-cw-ready', 'true');
    var P = makeProjection(meta);

    var markersEl = root.querySelector('[data-gi-cw-markers]');
    var overlay = root.querySelector('[data-gi-cw-overlay]');
    var filtersEl = root.querySelector('[data-gi-cw-filters]');
    var countEl = root.querySelector('[data-gi-cw-count]');
    var emptyEl = root.querySelector('[data-gi-cw-empty]');
    var searchEl = root.querySelector('[data-gi-cw-search]');
    var suggestEl = root.querySelector('[data-gi-cw-suggest]');
    var noResEl = root.querySelector('[data-gi-cw-noresults]');
    var clearEl = root.querySelector('[data-gi-cw-clear]');
    var panelRegion = root.querySelector('[data-gi-cw-panel-region]');
    var zoomEl = root.querySelector('[data-gi-cw-zoomctl]');
    var scaleEl = root.querySelector('[data-gi-cw-scale]');
    var scaleBar = root.querySelector('[data-gi-cw-scale-bar]');
    var scaleLabel = root.querySelector('[data-gi-cw-scale-label]');
    var labelsEl = root.querySelector('[data-gi-cw-labels]');
    var labelText = {};
    if (labelsEl) { try { labelText = JSON.parse(labelsEl.textContent); } catch (e) { labelText = {}; } }
    var lang = (document.documentElement.lang || 'en').toLowerCase();

    /* ── Partners ── */
    var partners = [];
    var jsonNodes = root.querySelectorAll('[data-gi-cw-partner-json]');
    for (var i = 0; i < jsonNodes.length; i++) {
      try {
        var d = JSON.parse(jsonNodes[i].textContent);
        d.id = String(d.id);
        d.lat = parseFloat(d.lat);
        d.lon = parseFloat(d.lon);
        if (!isFinite(d.lat) || !isFinite(d.lon)) continue;
        d.xy = P.project(d.lon, d.lat);
        d.exp = experienceOf(d.category);
        d.founding = d.founding === true || d.founding === 'true';
        d.hay = [d.name, d.city, d.region, d.categoryLabel, norm(d.city) === 'jeonju' ? labelText.jeonjuAlias : ''].filter(Boolean).map(norm).join(' | ');
        partners.push(d);
      } catch (e) { /* one bad block must not take the map down */ }
    }
    var initialId = partners.length ? partners[0].id : null;
    var state = { exp: 'all', q: '', selected: initialId };

    /* ── Province wash: any province containing an active partner ── */
    (function tintRegions() {
      var provs = svg.querySelectorAll('[data-gi-cw-provinces] path');
      if (!provs.length || !svg.createSVGPoint) return;
      var pt = svg.createSVGPoint();
      partners.forEach(function (p) {
        pt.x = p.xy[0]; pt.y = p.xy[1];
        for (var j = 0; j < provs.length; j++) {
          try { if (provs[j].isPointInFill(pt)) { provs[j].classList.add('is-partner-region'); break; } } catch (e) {}
        }
      });
    })();

    /* ── Camera ── */
    var cam = { cx: 0, cy: 0, s: 1 };
    var size = { w: 1, h: 1 };
    function measure() {
      var r = viewport.getBoundingClientRect();
      size.w = Math.max(1, r.width);
      size.h = Math.max(1, r.height);
    }
    function padPx() { return size.w < 600 ? 14 : 28; }
    /* Furthest out: the whole of South Korea, never more. */
    function fitSubject() {
      var pad = padPx();
      var w = Math.max(1, size.w - pad * 2), h = Math.max(1, size.h - pad * 2);
      var s = Math.max((subject[2] - subject[0]) / w, (subject[3] - subject[1]) / h);
      return { cx: (subject[0] + subject[2]) / 2, cy: (subject[1] + subject[3]) / 2, s: s };
    }
    function sMax() { return fitSubject().s; }
    /* Closest: about 12 km across the narrower side of the map. */
    function sMin() { return (P.unitsPerKm(36) * 12) / Math.min(size.w, size.h); }
    function constrain(c) {
      c.s = clamp(c.s, sMin(), sMax());
      var hw = (size.w * c.s) / 2, hh = (size.h * c.s) / 2;
      c.cx = meta.W - hw < hw ? meta.W / 2 : clamp(c.cx, hw, meta.W - hw);
      c.cy = meta.H - hh < hh ? meta.H / 2 : clamp(c.cy, hh, meta.H - hh);
      return c;
    }
    function apply() {
      constrain(cam);
      var w = size.w * cam.s, h = size.h * cam.s;
      svg.setAttribute('viewBox', [cam.cx - w / 2, cam.cy - h / 2, w, h].map(function (v) { return v.toFixed(3); }).join(' '));
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      placeOverlay();
      updateScale();
      updateZoomButtons();
    }
    function toScreen(xy) { return [(xy[0] - cam.cx) / cam.s + size.w / 2, (xy[1] - cam.cy) / cam.s + size.h / 2]; }
    function toMap(px, py) { return [cam.cx + (px - size.w / 2) * cam.s, cam.cy + (py - size.h / 2) * cam.s]; }

    var anim = null;
    function flyTo(target) {
      if (anim) { cancelAnimationFrame(anim.raf); anim = null; }
      constrain(target);
      if (reduceMotion) { cam = target; apply(); return; }
      var from = { cx: cam.cx, cy: cam.cy, s: cam.s };
      var t0 = performance.now(), dur = 600;
      anim = {};
      (function step(now) {
        var t = clamp((now - t0) / dur, 0, 1);
        var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        cam = {
          cx: from.cx + (target.cx - from.cx) * e,
          cy: from.cy + (target.cy - from.cy) * e,
          s: Math.exp(Math.log(from.s) + (Math.log(target.s) - Math.log(from.s)) * e)
        };
        apply();
        if (t < 1) anim.raf = requestAnimationFrame(step); else anim = null;
      })(t0);
    }
    function zoomAt(factor, px, py) {
      var before = toMap(px, py);
      cam.s = clamp(cam.s / factor, sMin(), sMax());
      var after = toMap(px, py);
      cam.cx += before[0] - after[0];
      cam.cy += before[1] - after[1];
      apply();
    }
    /* Bring a partner into view: pan only if it is near or past the edge,
       or zoom to a regional view of it when asked (search, Show on map). */
    function reveal(p, closeIn) {
      if (closeIn) {
        flyTo({ cx: p.xy[0], cy: p.xy[1], s: Math.min(cam.s, sMax() / 2.4) });
        return;
      }
      var sp = toScreen(p.xy), m = 70;
      if (sp[0] < m || sp[0] > size.w - m || sp[1] < m || sp[1] > size.h - m) flyTo({ cx: p.xy[0], cy: p.xy[1], s: cam.s });
    }

    /* ── Reference labels (SVG, sized in px) ── */
    var SVGNS = 'http://www.w3.org/2000/svg';
    var refs = [];
    REFERENCE.forEach(function (r) {
      var text = labelText[r.key];
      if (!text) return;
      var g = document.createElementNS(SVGNS, 'g');
      g.setAttribute('class', 'gi-cw-atlas__label gi-cw-atlas__label--' + r.kind);
      var t = document.createElementNS(SVGNS, 'text');
      t.textContent = text;
      if (r.kind === 'town') {
        var dot = document.createElementNS(SVGNS, 'circle');
        dot.setAttribute('r', '2.2');
        g.appendChild(dot);
        t.setAttribute('x', '6');
        t.setAttribute('y', '4');
      } else {
        /* island names sit under the island, centred */
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('y', '30');
      }
      g.appendChild(t);
      overlay.appendChild(g);
      refs.push({ g: g, xy: P.project(r.lon, r.lat) });
    });

    /* ── Partner markers (HTML buttons over the map) ── */
    var pins = {};
    var suppressClick = false;
    partners.forEach(function (p) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'gi-cw-pin' + (p.founding ? ' gi-cw-pin--founding' : '');
      b.setAttribute('data-id', p.id);
      b.setAttribute('aria-label', p.name + (p.city ? ', ' + p.city : ''));
      b.setAttribute('aria-pressed', 'false');
      var lab = document.createElement('span');
      lab.className = 'gi-cw-pin__label';
      lab.setAttribute('aria-hidden', 'true');
      var n = document.createElement('span');
      n.className = 'gi-cw-pin__name';
      n.textContent = p.name;
      var m = document.createElement('span');
      m.className = 'gi-cw-pin__meta';
      m.textContent = [p.city, p.categoryLabel].filter(Boolean).join(' · ');
      lab.appendChild(n);
      lab.appendChild(m);
      b.appendChild(lab);
      b.addEventListener('click', function () {
        if (suppressClick || b.getAttribute('data-dim') === 'true') return;
        select(p.id);
        reveal(p, false);
      });
      markersEl.appendChild(b);
      pins[p.id] = { el: b, xy: p.xy, labelEl: lab };
    });

    function placeOverlay() {
      refs.forEach(function (r) {
        r.g.setAttribute('transform', 'translate(' + r.xy[0] + ' ' + r.xy[1] + ') scale(' + cam.s + ')');
      });
      var zoomed = sMax() / cam.s;
      Object.keys(pins).forEach(function (id) {
        var pin = pins[id];
        var sp = toScreen(pin.xy);
        pin.el.style.transform = 'translate(' + sp[0].toFixed(1) + 'px,' + sp[1].toFixed(1) + 'px)';
        pin.el.style.visibility = sp[0] > -30 && sp[0] < size.w + 30 && sp[1] > -30 && sp[1] < size.h + 30 ? '' : 'hidden';
        /* Names: always for the selected partner; for the rest while there
           are few partners, or once the map is zoomed in. */
        var showLabel = id === state.selected || partners.length <= 4 || zoomed >= 2;
        pin.el.setAttribute('data-label', showLabel ? 'on' : 'off');
        var labW = pin.labelEl.offsetWidth || 140;
        pin.el.setAttribute('data-side', sp[0] + 34 + labW > size.w - 8 ? (sp[0] - 34 - labW > 8 ? 'left' : 'below') : 'right');
      });
    }

    /* ── Scale bar ── */
    var NICE = [1, 2, 5, 10, 20, 25, 50, 100, 200];
    function updateScale() {
      if (!scaleEl || !scaleBar) return;
      var pxPerKm = P.unitsPerKm(P.latAt(cam.cy, cam.cx)) / cam.s;
      var km = NICE[0];
      for (var i = 0; i < NICE.length; i++) if (NICE[i] * pxPerKm <= 110) km = NICE[i];
      scaleBar.style.width = Math.round(km * pxPerKm) + 'px';
      var num;
      try { num = new Intl.NumberFormat(lang).format(km); } catch (e) { num = String(km); }
      scaleLabel.textContent = num + ' km';
    }
    function updateZoomButtons() {
      if (!zoomEl) return;
      var zin = zoomEl.querySelector('[data-gi-cw-zoom="in"]');
      var zout = zoomEl.querySelector('[data-gi-cw-zoom="out"]');
      if (zin) zin.disabled = cam.s <= sMin() * 1.001;
      if (zout) zout.disabled = cam.s >= sMax() * 0.999;
    }

    /* ── Selection, search, filters ── */
    function byId(id) {
      for (var i = 0; i < partners.length; i++) if (partners[i].id === id) return partners[i];
      return null;
    }
    function cities() {
      var seen = {}, out = [];
      partners.forEach(function (p) {
        if (!p.city || seen[norm(p.city)]) return;
        seen[norm(p.city)] = 1;
        out.push(p.city);
      });
      return out;
    }
    function visiblePartners() {
      var q = norm(state.q);
      return partners.filter(function (p) {
        if (state.exp !== 'all' && p.exp !== state.exp) return false;
        if (q && p.hay.indexOf(q) === -1) return false;
        return true;
      });
    }
    function updateFilters() {
      if (filtersEl) {
        var bs = filtersEl.querySelectorAll('[data-gi-cw-filter]');
        for (var i = 0; i < bs.length; i++) bs[i].setAttribute('aria-pressed', bs[i].getAttribute('data-gi-cw-filter') === state.exp ? 'true' : 'false');
      }
      var n = visiblePartners().length;
      if (countEl) {
        var one = countEl.getAttribute('data-count-one'), other = countEl.getAttribute('data-count-other');
        countEl.textContent = n === 1 && one ? one : other ? other.replace('__N__', n) : n + (n === 1 ? ' partner' : ' partners');
      }
      if (noResEl) noResEl.hidden = n !== 0;
    }
    function updateSuggestions() {
      if (!suggestEl) return;
      var q = norm(state.q);
      var list = cities().filter(function (c) { return !q || norm(c).indexOf(q) !== -1; });
      if (list.length < 2 && !q) list = cities();
      suggestEl.textContent = '';
      list.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'gi-cw-map__city';
        b.setAttribute('data-gi-cw-city', c);
        b.setAttribute('aria-pressed', norm(state.q) === norm(c) ? 'true' : 'false');
        b.textContent = c;
        suggestEl.appendChild(b);
      });
      suggestEl.hidden = list.length === 0;
    }
    function updateMarkers() {
      var ids = visiblePartners().map(function (p) { return p.id; });
      Object.keys(pins).forEach(function (id) {
        var el = pins[id].el;
        var dim = ids.indexOf(id) === -1;
        el.setAttribute('data-dim', dim ? 'true' : 'false');
        el.setAttribute('aria-pressed', id === state.selected ? 'true' : 'false');
        if (dim) el.setAttribute('aria-disabled', 'true'); else el.removeAttribute('aria-disabled');
      });
      placeOverlay();
    }
    function updatePanel() {
      var panels = root.querySelectorAll('[data-gi-cw-panel]');
      var matched = false;
      for (var i = 0; i < panels.length; i++) {
        var isMatch = panels[i].getAttribute('data-partner-id') === state.selected;
        panels[i].hidden = !isMatch;
        if (isMatch) { matched = true; initGallery(panels[i]); }
      }
      if (emptyEl) emptyEl.hidden = matched;
      if (panelRegion) panelRegion.setAttribute('data-selected', matched ? 'true' : 'false');
    }
    function select(id) {
      state.selected = id;
      updateMarkers();
      updatePanel();
    }
    function reconcile(fromSearch) {
      var visible = visiblePartners();
      if (state.selected && !visible.some(function (p) { return p.id === state.selected; })) state.selected = null;
      if (visible.length === 1) state.selected = visible[0].id;
      updateFilters();
      updateSuggestions();
      updateMarkers();
      updatePanel();
      if (fromSearch && norm(state.q) && visible.length === 1) reveal(visible[0], true);
    }

    if (filtersEl) filtersEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-gi-cw-filter]');
      if (!b) return;
      state.exp = b.getAttribute('data-gi-cw-filter');
      reconcile(false);
    });
    if (searchEl) {
      searchEl.addEventListener('input', function () { state.q = searchEl.value; reconcile(true); });
      searchEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });
    }
    if (suggestEl) suggestEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-gi-cw-city]');
      if (!b) return;
      var city = b.getAttribute('data-gi-cw-city');
      var next = norm(state.q) === norm(city) ? '' : city;
      if (searchEl) searchEl.value = next;
      state.q = next;
      reconcile(true);
    });
    if (clearEl) clearEl.addEventListener('click', function () {
      if (searchEl) searchEl.value = '';
      state.q = '';
      state.exp = 'all';
      reconcile(false);
      if (searchEl) searchEl.focus();
    });

    /* ── Gallery ── */
    function initGallery(card) {
      var gallery = card.querySelector('[data-gi-cw-gallery]');
      if (!gallery || gallery.getAttribute('data-gi-ready') === 'true') return;
      var track = gallery.querySelector('[data-gi-cw-track]');
      var prev = gallery.querySelector('[data-gi-cw-prev]');
      var next = gallery.querySelector('[data-gi-cw-next]');
      var fracEl = gallery.querySelector('[data-gi-cw-frac]');
      if (!track) return;
      var slides = track.querySelectorAll('.gi-cw-card__slide');
      if (slides.length < 2 || !prev || !next) return;
      gallery.setAttribute('data-gi-ready', 'true');
      function index() { return Math.round(track.scrollLeft / (track.clientWidth || 1)); }
      function sync() {
        var i = index();
        prev.disabled = i <= 0;
        next.disabled = i >= slides.length - 1;
        if (fracEl) fracEl.textContent = (i + 1) + ' / ' + slides.length;
      }
      prev.addEventListener('click', function () { track.scrollBy({ left: -track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' }); });
      next.addEventListener('click', function () { track.scrollBy({ left: track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' }); });
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { sync(); ticking = false; });
      });
      window.addEventListener('resize', sync);
      sync();
    }

    /* ── View list / Show on map ── */
    var directory = document.querySelector('.gi-cw-dir');
    var viewList = root.querySelector('[data-gi-cw-viewlist]');
    if (viewList) {
      if (!directory) viewList.hidden = true;
      else viewList.addEventListener('click', function () {
        directory.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        var h = directory.querySelector('.gi-cw-dir__heading');
        if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
      });
    }
    document.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-gi-cw-show]');
      if (!b) return;
      var p = byId(b.getAttribute('data-gi-cw-show'));
      if (!p) return;
      if (searchEl) searchEl.value = '';
      state.q = '';
      state.exp = 'all';
      reconcile(false);
      select(p.id);
      root.querySelector('.gi-cw-atlas').scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      reveal(p, true);
      if (pins[p.id]) pins[p.id].el.focus({ preventScroll: true });
    });

    /* ── Pointer, wheel and keyboard ── */
    var pointers = {};
    var drag = null;
    viewport.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);
      if (ids.length === 1) {
        drag = { x: e.clientX, y: e.clientY, cx: cam.cx, cy: cam.cy, moved: false };
        suppressClick = false;
      } else if (ids.length === 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        drag = { pinch: Math.hypot(a.x - b.x, a.y - b.y), s: cam.s, moved: true };
        suppressClick = true;
      }
    });
    viewport.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId] || !drag) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);
      if (ids.length >= 2 && drag.pinch) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        var r = viewport.getBoundingClientRect();
        var mx = (a.x + b.x) / 2 - r.left, my = (a.y + b.y) / 2 - r.top;
        var before = toMap(mx, my);
        cam.s = clamp(drag.s * drag.pinch / Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), sMin(), sMax());
        var after = toMap(mx, my);
        cam.cx += before[0] - after[0];
        cam.cy += before[1] - after[1];
        apply();
        return;
      }
      var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 4) return;
      if (!drag.moved) {
        drag.moved = true;
        suppressClick = true;
        viewport.classList.add('is-dragging');
        try { viewport.setPointerCapture(e.pointerId); } catch (err) {}
        if (anim) { cancelAnimationFrame(anim.raf); anim = null; }
      }
      cam.cx = drag.cx - dx * cam.s;
      cam.cy = drag.cy - dy * cam.s;
      apply();
    });
    function endPointer(e) {
      delete pointers[e.pointerId];
      if (!Object.keys(pointers).length) {
        drag = null;
        viewport.classList.remove('is-dragging');
        setTimeout(function () { suppressClick = false; }, 0);
      }
    }
    viewport.addEventListener('pointerup', endPointer);
    viewport.addEventListener('pointercancel', endPointer);
    viewport.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') endPointer(e); });

    /* A plain wheel scrolls the page; trackpad pinch (ctrl+wheel) and
       ctrl/cmd+wheel zoom the map. */
    viewport.addEventListener('wheel', function (e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      var r = viewport.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });
    viewport.addEventListener('dblclick', function (e) {
      if (e.target.closest('.gi-cw-pin')) return;
      var r = viewport.getBoundingClientRect();
      zoomAt(2, e.clientX - r.left, e.clientY - r.top);
    });
    viewport.addEventListener('keydown', function (e) {
      if (e.target !== viewport) return;
      var step = 80, k = e.key;
      if (k === 'ArrowLeft') cam.cx -= step * cam.s;
      else if (k === 'ArrowRight') cam.cx += step * cam.s;
      else if (k === 'ArrowUp') cam.cy -= step * cam.s;
      else if (k === 'ArrowDown') cam.cy += step * cam.s;
      else if (k === '+' || k === '=') { zoomAt(1.6, size.w / 2, size.h / 2); e.preventDefault(); return; }
      else if (k === '-' || k === '_') { zoomAt(1 / 1.6, size.w / 2, size.h / 2); e.preventDefault(); return; }
      else if (k === '0' || k === 'Home') { resetMap(); e.preventDefault(); return; }
      else return;
      e.preventDefault();
      apply();
    });

    if (zoomEl) {
      zoomEl.hidden = false;
      zoomEl.addEventListener('click', function (e) {
        var z = e.target.closest('[data-gi-cw-zoom]');
        if (z) { zoomAt(z.getAttribute('data-gi-cw-zoom') === 'in' ? 1.8 : 1 / 1.8, size.w / 2, size.h / 2); return; }
        if (e.target.closest('[data-gi-cw-reset]')) resetMap();
      });
    }
    if (scaleEl) scaleEl.hidden = false;

    function resetMap() {
      if (initialId) select(initialId);
      flyTo(fitSubject());
    }

    /* ── The floating newsletter tab steps aside while the map is on
       screen at tablet and phone widths (see the section stylesheet). ── */
    var mq = window.matchMedia ? window.matchMedia('(max-width: 1023px)') : null;
    var onscreen = false;
    function syncLetter() {
      document.documentElement.classList.toggle('gi-cw-atlas-onscreen', !!(onscreen && mq && mq.matches));
    }
    if (mq && mq.addEventListener) mq.addEventListener('change', syncLetter);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { onscreen = en.intersectionRatio > 0.2; syncLetter(); });
      }, { threshold: [0, 0.2, 0.5] }).observe(viewport);
    }

    /* ── Start: fitted to South Korea ── */
    measure();
    reconcile(false);
    cam = fitSubject();
    apply();
    root.classList.add('is-live');

    var resizeRaf = null;
    function onResize() {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        var wasFitted = Math.abs(cam.s - sMax()) < sMax() * 0.01;
        measure();
        if (wasFitted) cam = fitSubject();
        apply();
      });
    }
    if (window.ResizeObserver) new ResizeObserver(onResize).observe(viewport);
    else window.addEventListener('resize', onResize);
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('.gi-cw-map');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
